import {
  Avatar,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Icon,
  Stack,
  Text,
  useColorModeValue,
  HStack,
} from '@chakra-ui/react';
import { Link as ReactLink } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import { FaStar, FaCheckCircle, FaArrowRight } from 'react-icons/fa';

import { BrandHeading } from './BrandHeading.jsx';
import { useUIStore } from '#store';
import FlickrImage from './FlickrImage';

const TestimonialCard = ({ children }) => {
  return (
    <Flex flex="1" direction="column" align="center">
      {children}
    </Flex>
  );
};

const TestimonialContent = ({ children }) => {
  return (
    <Stack
      bg={'white'}
      color="gray.800"
      boxShadow={'lg'}
      p={5}
      rounded={'xl'}
      align={'center'}
      pos={'relative'}
      _after={{
        content: `""`,
        w: 0,
        h: 0,
        borderLeft: 'solid transparent',
        borderLeftWidth: 16,
        borderRight: 'solid transparent',
        borderRightWidth: 16,
        borderTop: 'solid',
        borderTopWidth: 16,
        borderTopColor: 'white',
        pos: 'absolute',
        bottom: '-16px',
        left: '50%',
        transform: 'translateX(-50%)',
      }}
    >
      {children}
    </Stack>
  );
};

const TestimonialHeading = ({ children, permalink }) => {
  if (permalink) {
    return (
      <Heading as={'h3'} fontSize={'lg'} textAlign="center" noOfLines={2}>
        <Box
          as={ReactLink}
          to={permalink}
          color="brand.700"
          _hover={{
            color: 'brand.500',
            textDecoration: 'underline',
          }}
          transition="color 0.2s"
        >
          {children}
        </Box>
      </Heading>
    );
  }
  
  return (
    <Heading as={'h3'} fontSize={'lg'} textAlign="center" noOfLines={2}>
      {children}
    </Heading>
  );
};

const TestimonialText = ({ children }) => {
  return (
    <Text
      textAlign={'center'}
      color={useColorModeValue('gray.600', 'gray.400')}
      fontSize={'sm'}
      noOfLines={4}
    >
      {children}
    </Text>
  );
};

const TestimonialAvatar = ({ name, location }) => {
  return (
    <Flex align={'center'} mt={6} direction={'column'}>
      <FlickrImage
        text={location}
        size="lg"
        fallbackSrc="/public/camp.jpg"
        objectFit="cover"
        height="48px"
        width="48px"
        borderRadius="full"
        imageStyle={{
          border: '2px solid',
          borderColor: 'brand.200',
        }}
        mb={2}
      />
      <Stack spacing={0} align={'center'}>
        <HStack spacing={1}>
          <Text fontWeight={600} fontSize="sm">
            {name}
          </Text>
          <Icon as={FaCheckCircle} color="green.500" boxSize={3} />
        </HStack>
        <Text
          fontSize={'xs'}
          color={useColorModeValue('gray.500', 'gray.400')}
          noOfLines={1}
        >
          {location}
        </Text>
      </Stack>
    </Flex>
  );
};

const StarRating = () => (
  <HStack spacing={1} mb={2}>
    {[...Array(5)].map((_, i) => (
      <Icon key={i} as={FaStar} color="yellow.400" boxSize={3} />
    ))}
  </HStack>
);

export default function WithSpeechBubbles() {
  const { testimonials, fetchTestimonials } = useUIStore(
    (state) => ({
      testimonials: state.testimonials,
      fetchTestimonials: state.fetchTestimonials,
    }),
    shallow
  );

  // All hooks must be called before any early returns
  const bgColor = useColorModeValue('gray.100', 'gray.700');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');

  useEffect(() => {
    if (!testimonials?.length) {
      fetchTestimonials();
    }
  }, []);

  // Show 3 random testimonials on landing page
  const displayTestimonials = useMemo(() => {
    if (!testimonials?.length) return [];
    const shuffled = [...testimonials].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 3);
  }, [testimonials]);

  if (!displayTestimonials.length) {
    return null;
  }

  return (
    <Box bg={bgColor}>
      <Container maxW={'7xl'} py={[12, 16]} as={Stack} spacing={12}>
        <Stack spacing={2} align={'center'}>
          <BrandHeading fontSize={{ base: '3xl', sm: '4xl', lg: '5xl' }} mb={2}>
            What Our Clients Say
          </BrandHeading>
          <Text fontSize={['md', 'lg']} color={subtitleColor}>
            Join thousands who've secured their dream reservations
          </Text>
        </Stack>
        <Stack
          direction={{ base: 'column', md: 'row' }}
          spacing={{ base: 10, md: 4, lg: 10 }}
          align="flex-start"
        >
          {displayTestimonials.map((testimonial, index) => (
            <TestimonialCard key={testimonial._id || index}>
              <TestimonialContent>
                <StarRating />
                <TestimonialHeading permalink={testimonial.tracker?.outingId?.permalink}>
                  {testimonial.tracker?.outingId?.name || 'Great Experience'}
                </TestimonialHeading>
                <TestimonialText>"{testimonial.description}"</TestimonialText>
              </TestimonialContent>
              <TestimonialAvatar
                name={testimonial.name}
                location={
                  testimonial.tracker?.outingId?.parent_name ||
                  testimonial.tracker?.outingId?.name ||
                  ''
                }
              />
            </TestimonialCard>
          ))}
        </Stack>
        <Flex justify="center">
          <Button
            as={ReactLink}
            to="/testimonials"
            colorScheme="green"
            variant="outline"
            size="lg"
            rightIcon={<Icon as={FaArrowRight} />}
            _hover={{
              bg: 'green.500',
              color: 'white',
            }}
          >
            Read More Reviews
          </Button>
        </Flex>
      </Container>
    </Box>
  );
}
